import { useState, useCallback, useRef, useEffect } from "preact/hooks";
import { t } from "../lib/i18n.js";
import { API_BASE } from "../lib/api-base.js";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  streaming?: boolean;
  error?: boolean;
  sources?: { filename: string; score: number }[];
  createdAt?: number; // Unix ms — for relative timestamps
}

export function parseSSELine(line: string): Record<string, any> | null {
  if (!line.startsWith("data: ")) return null;
  try {
    return JSON.parse(line.slice(6));
  } catch {
    return null;
  }
}

export function createSessionId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return `s_${Array.from(bytes).map(b => b.toString(16).padStart(2, "0")).join("")}`;
}

export interface VisitorInfo {
  name: string;
  email: string;
  [key: string]: string;
}

export interface UseChatOptions {
  pageUrl?: string;
  utmParams?: Record<string, string> | null;
  referrer?: string;
  anonymousId?: string;
}

// ── localStorage helpers (scoped by tenant) ──

// A returning visitor resumes their previous conversation only within this
// window; after 48h of inactivity the widget opens a fresh chat window (new
// session → server creates a new conversation). Mirrors the server-side
// ChatService.REOPEN_WINDOW_MS guard. Product requirement: "new chat window
// opens after 48 hours".
const SESSION_TTL_MS = 48 * 60 * 60 * 1000; // 48 hours

/**
 * True when the last activity is older than the 48h window — i.e. the visitor
 * is "returning after 48 hours" and should get a fresh chat window rather than
 * resuming the previous conversation. A missing/invalid timestamp (legacy
 * sessions saved before this field existed) is treated as not-expired.
 */
export function isSessionExpired(lastSeen: unknown, now: number = Date.now()): boolean {
  return typeof lastSeen === "number" && now - lastSeen > SESSION_TTL_MS;
}

function storageKey(tenantId: string, key: string): string {
  return `canary_${key}_${tenantId}`;
}

function loadFromStorage<T>(tenantId: string, key: string): T | null {
  try {
    const raw = localStorage.getItem(storageKey(tenantId, key));
    return raw ? JSON.parse(raw) as T : null;
  } catch { return null; }
}

function saveToStorage(tenantId: string, key: string, value: unknown): void {
  try { localStorage.setItem(storageKey(tenantId, key), JSON.stringify(value)); } catch {}
}

function clearStorage(tenantId: string): void {
  try {
    localStorage.removeItem(storageKey(tenantId, "conv"));
    localStorage.removeItem(storageKey(tenantId, "msgs"));
    localStorage.removeItem(storageKey(tenantId, "session"));
    localStorage.removeItem(storageKey(tenantId, "lastseen"));
  } catch {}
}

export function useChat(tenantId: string, language = "en", options?: UseChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const loadedRef = useRef(false);

  // Load persisted state once tenantId is available
  useEffect(() => {
    if (!tenantId || loadedRef.current) return;
    loadedRef.current = true;

    // Expire the previous conversation after 48h of inactivity. Returning
    // beyond the window opens a fresh window: new session id (so the server
    // starts a new conversation) and an empty transcript.
    const lastSeen = loadFromStorage<number>(tenantId, "lastseen");
    if (isSessionExpired(lastSeen)) {
      clearStorage(tenantId);
      sessionIdRef.current = createSessionId();
      saveToStorage(tenantId, "session", sessionIdRef.current);
      if (typeof sessionStorage !== "undefined") {
        try { sessionStorage.setItem("canary_session", sessionIdRef.current); } catch {}
      }
      return; // skip restoring the stale conversation
    }

    // Load session from tenant-scoped localStorage (overrides sessionStorage fallback)
    const savedSession = loadFromStorage<string>(tenantId, "session");
    if (savedSession) {
      sessionIdRef.current = savedSession;
    } else {
      // First time for this tenant — persist current session
      saveToStorage(tenantId, "session", sessionIdRef.current);
    }

    const savedConv = loadFromStorage<string>(tenantId, "conv");
    if (savedConv) conversationIdRef.current = savedConv;

    const saved = loadFromStorage<ChatMessage[]>(tenantId, "msgs");
    if (saved && saved.length > 0) {
      setMessages(saved);
    }
  }, [tenantId]);
  const storedVisitor = typeof sessionStorage !== "undefined"
    ? (() => { try { const v = sessionStorage.getItem("canary_visitor"); return v ? JSON.parse(v) as VisitorInfo : null; } catch { return null; } })()
    : null;
  const visitorRef = useRef<VisitorInfo | null>(storedVisitor);
  const abortRef = useRef<AbortController | null>(null);

  // Session ID — try localStorage (tenant-scoped), fall back to sessionStorage (legacy)
  const sessionIdRef = useRef<string>(
    (typeof sessionStorage !== "undefined" && sessionStorage.getItem("canary_session")) || ""
  );
  const conversationIdRef = useRef<string>("");

  // Load session from tenant-scoped localStorage once tenantId is available (in the same effect as messages)
  // Generate new session ID if none exists
  if (!sessionIdRef.current) {
    sessionIdRef.current = createSessionId();
  }
  // Persist to sessionStorage for backward compat
  if (typeof sessionStorage !== "undefined") {
    try { sessionStorage.setItem("canary_session", sessionIdRef.current); } catch {}
  }

  /** Persist current messages + conversationId + session to localStorage */
  function persistChat(msgs: ChatMessage[], convId: string) {
    if (!tenantId) return;
    const toSave = msgs.filter(m => !m.streaming).map(({ streaming: _, ...m }) => m);
    saveToStorage(tenantId, "msgs", toSave);
    saveToStorage(tenantId, "conv", convId);
    saveToStorage(tenantId, "session", sessionIdRef.current);
    // Roll the 48h inactivity window forward on each persisted exchange.
    saveToStorage(tenantId, "lastseen", Date.now());
  }

  const setVisitor = useCallback((info: VisitorInfo) => {
    visitorRef.current = info;
    if (typeof sessionStorage !== "undefined") {
      try { sessionStorage.setItem("canary_visitor", JSON.stringify(info)); } catch { /* storage full */ }
    }
  }, []);

  /** Clear chat and start fresh */
  const resetChat = useCallback(() => {
    clearStorage(tenantId);
    conversationIdRef.current = "";
    // New session so server creates a new conversation (not reopen the old one)
    sessionIdRef.current = createSessionId();
    saveToStorage(tenantId, "session", sessionIdRef.current);
    if (typeof sessionStorage !== "undefined") {
      try { sessionStorage.setItem("canary_session", sessionIdRef.current); } catch {}
    }
    setMessages([]);
    setSuggestions([]);
  }, [tenantId]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || streaming) return;

      const userMsg: ChatMessage = {
        id: `msg_${Date.now()}`,
        role: "user",
        content: text.trim(),
        createdAt: Date.now(),
      };

      const assistantMsg: ChatMessage = {
        id: `msg_${Date.now()}_a`,
        role: "assistant",
        content: "",
        streaming: true,
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setStreaming(true);
      setSuggestions([]);

      const controller = new AbortController();
      abortRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), 60_000);

      try {
        const body: Record<string, unknown> = {
          tenant_id: tenantId,
          session_id: sessionIdRef.current,
          message: text.trim(),
        };

        // Attach visitor info + page context on first message
        if (visitorRef.current || !conversationIdRef.current) {
          const meta: Record<string, unknown> = {};
          if (visitorRef.current) {
            meta.visitor = visitorRef.current;
            visitorRef.current = null; // Only send once
          }
          if (options?.pageUrl) meta.page_url = options.pageUrl;
          if (options?.utmParams) meta.utm_params = options.utmParams;
          if (options?.referrer) meta.referrer = options.referrer;
          if (options?.anonymousId) meta.anonymous_id = options.anonymousId;
          if (Object.keys(meta).length > 0) body.metadata = meta;
        }

        const res = await fetch(`${API_BASE}/api/chat/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          let errorMsg = "Something went wrong. Please try again.";
          try {
            const errBody = await res.json();
            if (errBody.message) errorMsg = errBody.message;
          } catch { /* not JSON */ }
          throw new Error(errorMsg);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            const event = parseSSELine(line.trim());
            if (!event) continue;

            if (event.type === "metadata") {
              conversationIdRef.current = event.conversationId;
            }

            if (event.type === "delta") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    content: last.content + event.text,
                  };
                }
                return updated;
              });
            }

            if (event.type === "replace") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    content: event.text,
                  };
                }
                return updated;
              });
            }

            if (event.type === "status") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant" && !last.content) {
                  updated[updated.length - 1] = {
                    ...last,
                    content: event.text,
                    streaming: true,
                  };
                }
                return updated;
              });
            }

            if (event.type === "error") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    content: event.message || t("error.generic", language),
                    streaming: false,
                    error: true,
                  };
                }
                return updated;
              });
            }

            if (event.type === "done") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    streaming: false,
                    createdAt: Date.now(),
                    sources: Array.isArray(event.sources) ? event.sources : undefined,
                  };
                }
                // Persist after stream completes
                persistChat(updated, conversationIdRef.current);
                return updated;
              });
            }

            if (event.type === "suggestions") {
              if (Array.isArray(event.suggestions) && event.suggestions.length > 0) {
                setSuggestions(event.suggestions);
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name === "AbortError") {
          // Timeout — show feedback instead of silently swallowing
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && last.role === "assistant") {
              updated[updated.length - 1] = {
                ...last,
                content: t("error.timeout", language),
                streaming: false,
                error: true,
              };
            }
            return updated;
          });
          return;
        }
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === "assistant") {
            updated[updated.length - 1] = {
              ...last,
              content: error.message && error.message !== "Stream request failed"
                ? error.message
                : t("error.generic", language),
              streaming: false,
              error: true,
            };
          }
          return updated;
        });
      } finally {
        clearTimeout(timeoutId);
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [tenantId, streaming, language]
  );

  const retryLast = useCallback(() => {
    // Find the last user message before the failed assistant message
    const lastUserMsg = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUserMsg) return;
    // Remove the failed assistant message
    setMessages((prev) => prev.filter((m) => !m.error));
    sendMessage(lastUserMsg.content);
  }, [messages, sendMessage]);

  return { messages, streaming, sendMessage, retryLast, setVisitor, resetChat, conversationId: conversationIdRef.current, sessionId: sessionIdRef.current, suggestions };
}
